import type { MaterialsRow } from "@workspace/api-zod/materials-list";

type PurchaseUnit = { unit: string; size: number; whole: boolean; missing: boolean };
const norm = (text: string) => text.toLowerCase().replace(/²/g, "2").replace(/³/g, "3").replace(/\s+/g, " ").trim();
export const materialMeasureUnit = (text: string) => {
  const value = norm(text).replace(/\./g, "");
  if (/^(sqm|m2|sq m|square metres?|square meters?)$/.test(value)) return "m²";
  if (/^(lm|lin m|linear metres?|linear meters?|metres?|meters?|m)$/.test(value)) return "m";
  if (/^(kilograms?|kgs?|kg)$/.test(value)) return "kg";
  if (/^(tonnes?|t)$/.test(value)) return "tonne";
  if (/^(litres?|liters?|l)$/.test(value)) return "litre";
  if (/^(each|ea|no|nos|number|units?)$/.test(value)) return "each";
  return value.replace(/s$/, "");
};
const unique = (values: number[]) => {
  const sizes = [...new Set(values.filter(n => Number.isFinite(n) && n > 0).map(n => Number(n.toFixed(6))))];
  return sizes.length === 1 ? sizes[0] : undefined;
};
const metres = (value: number, suffix: string) => value / (suffix === "mm" ? 1000 : suffix === "cm" ? 100 : 1);
const dimensions = /(\d+(?:\.\d+)?)\s*(mm|cm|m)?\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(mm|cm|m)?(?:\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(mm|cm|m)?)?/gi;

function sheetArea(text: string) {
  const areas: number[] = [];
  for (const match of text.matchAll(dimensions)) {
    const values = [Number(match[1]), Number(match[3]), ...(match[5] ? [Number(match[5])] : [])];
    // Unlabelled 2400 x 1200 product sizes are standard millimetre notation.
    const shared = match[6] ?? match[4] ?? match[2] ?? (values.filter(n => n >= 100).length >= 2 ? "mm" : undefined);
    if (!shared) continue;
    const sizes = values.map((n, i) => metres(n, match[[2, 4, 6][i]] ?? shared)).sort((a, b) => b - a);
    if (sizes[0] >= .1 && sizes[1] >= .1) areas.push(sizes[0] * sizes[1]);
  }
  return unique(areas);
}

function stockLength(text: string) {
  const withoutSections = text.replace(dimensions, " ");
  return unique([...withoutSections.matchAll(/(\d+(?:\.\d+)?)\s*(mm|cm|m)\b/gi)]
    .map(match => metres(Number(match[1]), match[2].toLowerCase())).filter(n => n >= .5));
}

function packageKind(text: string, base: string) {
  if (/\b(?:sheets?|plasterboard|wallboard|plywood|osb|mdf|chipboard|backer\s*board|cement\s*board)\b/.test(text)) return "sheet";
  if (base === "m²" && /\b(?:boards?|pir|eps|xps|phenolic|rigid\s+insulation)\b/.test(text)) return "board";
  if (/\b(?:rolls?|membrane|polythene|felt|tape|vapour)\b/.test(text)) return "roll";
  if (/\b(?:packs?|tiles?|bricks?|blocks?)\b/.test(text)) return "pack";
  if (base === "m" && /\b(?:timber|battens?|skirting|architraves?|pipes?|conduit|lengths?|rails?|joists?|studs?|beams?|moulding|decking)\b/.test(text)) return "length";
  if (/\b(?:bags?|cement|plaster|grout|mortar|powder|adhesive|sand)\b/.test(text)) return "bag";
  if (/\b(?:tubs?|tins?|buckets?|paint)\b/.test(text)) return "tub";
  if (/\bbottles?\b/.test(text)) return "bottle";
  return "pack";
}

/** Only convert from explicit stock dimensions, coverage or stored pack size.
 * No invented sheet defaults, material densities or coverage rates. */
export function materialPurchaseUnit(row: MaterialsRow): PurchaseUnit {
  const base = materialMeasureUnit(row.unit);
  const text = norm(`${row.name} ${row.spec}`);
  const physical = new Set(["each", "sheet", "board", "bag", "pack", "length", "roll", "tub", "tin", "bottle", "can", "cartridge", "box", "set", "kit", "pair", "brick", "block", "tile", "piece"]);
  if (physical.has(base)) return { unit: base, size: 1, whole: true, missing: false };
  const kind = packageKind(text, base);
  let size = row.packSize && row.packSize > 0 && Number.isFinite(row.packSize) ? row.packSize : undefined;
  // Coverage must identify the measured unit and the purchase container.
  if (!size) {
    const coverage: number[] = [];
    for (const match of text.matchAll(/(\d+(?:\.\d+)?)\s*(m2|sqm|kg|litres?|liters?|l|m)\s*(?:\/|per\s+)(?:a\s+)?(sheet|board|bag|pack|roll|length|tub|tin|bottle)\b/g)) {
      if (materialMeasureUnit(match[2]) === base && match[3] === kind) coverage.push(Number(match[1]));
    }
    for (const match of text.matchAll(/(?:covers?|coverage(?:\s+of)?\s*:?)\s*(\d+(?:\.\d+)?)\s*(m2|sqm|kg|litres?|liters?|l|m)\b/g)) {
      if (materialMeasureUnit(match[2]) === base) coverage.push(Number(match[1]));
    }
    size = unique(coverage);
  }
  if (!size && base === "m²" && ["sheet", "board", "roll"].includes(kind)) size = sheetArea(text);
  if (!size && base === "m" && ["length", "roll"].includes(kind)) size = stockLength(text);
  if (!size && ["kg", "tonne"].includes(base)) {
    size = unique([...text.matchAll(/(\d+(?:\.\d+)?)\s*(kg|g)\b(?!\s*(?:\/|per\b))/g)]
      .map(match => Number(match[1]) / (match[2] === "g" ? 1000 : 1) / (base === "tonne" ? 1000 : 1)));
  }
  if (!size && base === "litre") {
    size = unique([...text.matchAll(/(\d+(?:\.\d+)?)\s*(ml|litres?|liters?|l)\b/g)]
      .map(match => Number(match[1]) / (match[2] === "ml" ? 1000 : 1)));
  }
  if (size) return { unit: kind, size, whole: true, missing: false };
  // Loose bulk materials can legitimately be ordered by volume or weight.
  const packaged = base === "m²" ? kind !== "pack" || /\b(?:packs?|tiles?)\b/.test(text)
    : base === "m" ? kind === "length" || kind === "roll"
      : ["kg", "litre"].includes(base) && /\b(?:bags?|tubs?|tins?|packs?|bottles?|cement|plaster|adhesive|grout|paint)\b/.test(text);
  return { unit: row.unit, size: 1, whole: false, missing: packaged };
}

export function purchaseUnitLabel(value: string, quantity: number) {
  if (quantity === 1 || !/^(sheet|board|bag|pack|length|roll|tub|tin|bottle|can|cartridge|box|set|kit|pair|brick|block|tile|piece)$/.test(value)) return value;
  return value === "box" ? "boxes" : `${value}s`;
}
